/**
 * Générateur — Il mène des raisonnements en utilisant des propriétés des
 * figures, des configurations, de la rotation et de l'homothétie.
 * Attendu ID : 243 | Niveau : 3ème | Domaine : Géométrie plane (d17, O6)
 *
 * Source doc prof (D4S2.Ex.2, D4S3.Ex.2, D4S1.Ex.12, D2S3.Ex.1-2). Plan
 * auto-validé 2026-07-18 (TODO §6) : « erreur » (l'erreur de Cédric : la
 * réciproque exige les alignements dans le même ordre), « mediatrice » (LA
 * démonstration du cluster : trois points équidistants de B et D → alignés
 * sur la médiatrice, drag), « rotationimage » (BG = BD et angle GBD = 60°
 * → l'image de G par la rotation de 60° est D), « translation » (nature de
 * l'image / conservation des aires), « croisement » (le contre-exemple qui
 * vaccine : la réciproque conclut sur les BONNES associations de points).
 */

import { randomChoice, randInt, shuffle, tex, makeQcm, makeTexteTrou } from '../../../utils';

const ATTENDU_ID = 243;

/** Rapport de longueurs en KaTeX. */
const rap = (num, den) => `\\dfrac{${num}}{${den}}`;
/** Décimal français protégé dans une formule KaTeX. */
const d = (s) => String(s).replace(',', '{,}');
const L_ERREUR = 'Où est l\'erreur de raisonnement ?';
const L_MEDIATRICE = 'Démontrer un alignement (médiatrice)';
const L_ROTATIONIMAGE = 'Trouver une image par rotation en raisonnant';
const L_TRANSLATION = 'Raisonner avec une translation';
const L_CROISEMENT = 'La réciproque conclut sur les bonnes droites';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const fr10 = (dix) => (dix % 10 === 0 ? String(dix / 10) : `${Math.trunc(dix / 10)},${Math.abs(dix % 10)}`);
const PRENOMS = ['Cédric', 'Manon', 'Yanis', 'Sofia', 'Timéo', 'Leïla'];

// ═════════ « erreur » — QCM ═════════

const CHOIX_ERREUR = [
    'Les calculs sont justes, mais la réciproque exige de vérifier l\'ORDRE des points sur les droites',
    'Les rapports ne sont pas égaux : le calcul est faux',
    'Il fallait utiliser le théorème de Thalès direct, pas sa réciproque',
    'Les fractions ne sont pas simplifiées, donc la comparaison est impossible',
];

const genErreurQcm = () => {
    const prenom = randomChoice(PRENOMS);
    // Rapports VRAIMENT égaux (p/q), en dixièmes exacts.
    const p = randomChoice([2, 3]);
    const q = p + randInt(1, 2);
    const m = randInt(11, 19);                              // EM = p·m/10, EF = q·m/10
    const n = randInt(11, 19);
    const em = p * m; const ef = q * m; const ep = p * n; const eg = q * n;
    const payload = makeQcm([...CHOIX_ERREUR], CHOIX_ERREUR[0],
        `<p>Sur la copie de ${prenom} : « ${tex(`${rap('EM', 'EF')} = ${rap(d(fr10(em)), d(fr10(ef)))} = ${rap(p, q)}`)} ; `
        + `${tex(`${rap('EP', 'EG')} = ${rap(d(fr10(ep)), d(fr10(eg)))} = ${rap(p, q)}`)} ; donc d'après la `
        + 'réciproque du théorème de Thalès, (PM) ∥ (FG). »</p>'
        // « (les points ne sont pas alignés dans le même ordre) » était la
        // réponse du QCM, écrite dans l'énoncé.
        + '<p>Or la figure montre que M et P ne sont PAS du même côté de E que F et G, et (PM) n\'est '
        + 'pas parallèle à (FG).</p>'
        + `<p><b>D'où vient l'erreur de raisonnement de ${prenom} ?</b></p>`,
        opts(L_ERREUR));
    payload._meta.params = { prenom, p, q, em, ef, ep, eg };
    return payload;
};

// ═════════ « mediatrice » — drag ═════════

const TRIPLES_PTS = [['A', 'G', 'C'], ['M', 'S', 'T'], ['K', 'L', 'P'], ['E', 'I', 'U'], ['N', 'O', 'W'], ['J', 'Q', 'Z']];
const PAIRES_SEG = [['B', 'D'], ['R', 'V'], ['F', 'H'], ['X', 'Y'], ['C', 'T']];

const genMediatriceDrag = () => {
    let [X, Y, Z] = randomChoice(TRIPLES_PTS);
    const [B, D] = randomChoice(PAIRES_SEG.filter(pp => ![X, Y, Z].includes(pp[0]) && ![X, Y, Z].includes(pp[1])));
    const phrase = `${X}${B} = ${X}${D} : le point ${X} est {{équidistant}} de ${B} et de ${D}, il appartient donc à la `
        + `{{médiatrice}} du segment [${B}${D}]. Les points ${Y} et ${Z} aussi. Or la médiatrice est une droite : `
        + `les points ${X}, ${Y} et ${Z} sont donc {{alignés}}.`;
    const payload = makeTexteTrou(phrase,
        `<p>Sur une figure, on a codé les égalités ${X}${B} = ${X}${D}, ${Y}${B} = ${Y}${D} et ${Z}${B} = ${Z}${D}. `
        + `On veut DÉMONTRER que ${X}, ${Y} et ${Z} sont alignés, sans mesurer.</p>`
        + '<p><b>Complète la démonstration.</b></p>',
        {
            ...opts(L_MEDIATRICE),
            mode: 'drag',
            distracteurs: ['bissectrice', 'parallèles', 'symétriques', 'le milieu'],
        });
    payload._meta.params = { X, Y, Z, B, D };
    return payload;
};

// ═════════ « rotationimage » — QCM ═════════

const genRotationImageQcm = () => {
    const centre = randomChoice(['B', 'O', 'S']);
    const lettres = shuffle(['A', 'E', 'G', 'D', 'C', 'H', 'K', 'M'].filter(l => l !== centre));
    const [dep1, arr1, dep2, arr2] = lettres;               // r envoie dep1→arr1 ; on cherche l'image de dep2
    const angle = randomChoice([45, 60, 72, 90, 120]);
    const payload = makeQcm([arr2, dep2, arr1, centre], arr2,
        `<p>r est la rotation de centre ${centre} qui envoie ${dep1} sur ${arr1} : on sait que `
        + `${centre}${dep1} = ${centre}${arr1} et que l'angle ${dep1}${centre}${arr1} mesure ${angle}°.</p>`
        + `<p>On sait aussi que ${centre}${dep2} = ${centre}${arr2} et que l'angle ${dep2}${centre}${arr2} `
        + `mesure ${angle}°, dans le MÊME sens de rotation.</p>`
        // « Une rotation est déterminée par son centre, son angle et son sens »
        // est exactement la propriété que l'exercice fait appliquer.
        + `<p><b>Quelle est l'image de ${dep2} par r ?</b></p>`,
        opts(L_ROTATIONIMAGE));
    payload._meta.params = { centre, dep1, arr1, dep2, arr2, angle };
    return payload;
};

// ═════════ « translation » — QCM ═════════

const genTranslationQcm = () => {
    const [A, C] = randomChoice([['A', 'C'], ['R', 'T'], ['M', 'P'], ['S', 'U'], ['G', 'I']]);
    const [B, E] = randomChoice([['B', 'E'], ['D', 'F'], ['K', 'L'], ['V', 'W'], ['H', 'J']].filter(pp => !pp.includes(A) && !pp.includes(C)));
    if (randInt(0, 1) === 1) {
        const correct = 'Un parallélogramme';
        const payload = makeQcm([correct, 'Un trapèze qui n\'est pas un parallélogramme', 'Forcément un losange', 'Forcément un rectangle'], correct,
            `<p>La translation qui envoie ${A} sur ${C} transforme ${B} en ${E}. On a donc ${A}${C} = ${B}${E} `
            + `et (${A}${C}) ∥ (${B}${E}) : le quadrilatère ${A}${C}${E}${B} a deux côtés opposés parallèles et `
            + 'de même longueur.</p>'
            + `<p><b>Quelle est la nature du quadrilatère ${A}${C}${E}${B} ?</b></p>`,
            opts(L_TRANSLATION));
        payload._meta.params = { A, C, B, E, question: 'nature' };
        return payload;
    }
    // Sans « : une translation conserve les aires » — c'était la seule
    // proposition justifiée, donc la seule à porter la propriété demandée.
    const correct = 'Elles sont égales';
    const payload = makeQcm([correct, 'L\'aire de l\'image est deux fois plus grande', 'L\'aire de l\'image est plus petite', 'On ne peut pas les comparer sans mesurer'], correct,
        `<p>La translation qui envoie ${A} sur ${C} transforme le triangle ${A}${B}${E} en son image. `
        + 'On compare l\'aire du triangle de départ et celle de son image.</p>'
        + '<p><b>Que peut-on dire des deux aires ?</b></p>',
        opts(L_TRANSLATION));
    payload._meta.params = { A, C, B, E, question: 'aires' };
    return payload;
};

// ═════════ « croisement » — le contre-exemple ═════════

const genCroisementQcm = () => {
    const A = randomChoice(['A', 'O', 'S']);
    const lettres = shuffle(['B', 'C', 'D', 'E', 'G', 'H', 'K'].filter(l => l !== A));
    const [B, C, D, E] = lettres;                           // A,B,C alignés ; A,D,E alignés ; AB/AC = AD/AE
    const correct = `(${B}${D}) ∥ (${C}${E})`;
    const payload = makeQcm([correct, `(${B}${E}) ∥ (${C}${D})`, `(${B}${C}) ∥ (${D}${E})`, 'Rien du tout'], correct,
        `<p>${A}, ${B}, ${C} sont alignés dans cet ordre, ainsi que ${A}, ${D}, ${E}. On sait que `
        + `${tex(`${rap(`${A}${B}`, `${A}${C}`)} = ${rap(`${A}${D}`, `${A}${E}`)}`)}.</p>`
        // « Les rapports égaux ASSOCIENT B à D et C à E » désignait la bonne
        // réponse : c'est la lecture des rapports qui est évaluée ici.
        + '<p><b>Quel parallélisme la réciproque du théorème de Thalès permet-elle de conclure ?</b></p>',
        opts(L_CROISEMENT));
    payload._meta.params = { A, B, C, D, E };
    return payload;
};

const GENERATORS = {
    erreur_qcm_unique: genErreurQcm,
    mediatrice_texte_trou_drag: genMediatriceDrag,
    rotationimage_qcm_unique: genRotationImageQcm,
    translation_qcm_unique: genTranslationQcm,
    croisement_qcm_unique: genCroisementQcm,
};
const FORMAT_LABELS = { qcm_unique: 'QCM', texte_trou_drag: 'Drag trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'erreur', label: L_ERREUR, formatsList: buildFormats('erreur') },
    { id: 'mediatrice', label: L_MEDIATRICE, formatsList: buildFormats('mediatrice') },
    { id: 'rotationimage', label: L_ROTATIONIMAGE, formatsList: buildFormats('rotationimage') },
    { id: 'translation', label: L_TRANSLATION, formatsList: buildFormats('translation') },
    { id: 'croisement', label: L_CROISEMENT, formatsList: buildFormats('croisement') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
